/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiLock } from 'react-icons/fi';
import * as Yup from 'yup';

import { FormHandles } from '@unform/core';
import { AuthLayout } from '../../../../components/AuthLayout';
import { InputLogin } from '../../../../components/InputLogin';

import {
  ButtonSignIn,
  ContainerLink,
  Form,
  Header,
  StatusTag,
  Title,
} from './styles';

import { useToast } from '../../../../hooks/toast';
import { useAuth } from '../../../../hooks/auth';
import { api } from '../../../../services/api';
import { getValidationErrors } from '../../../../utils/getValidationErrors';

interface ISignInProps {
  email: string;
  password: string;
}

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { signIn, signOut } = useAuth();
  const formRef = useRef<FormHandles>(null);

  useEffect(() => {
    document.title = 'TrenzoX';
    signOut();

    if (new URLSearchParams(window.location.search).get('session_expired')) {
      addToast({
        type: 'error',
        title: 'Sessão expirada',
        description: 'Faça login novamente para continuar.',
      });

      window.history.replaceState(null, '', '/sign-in');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(
    async (data: ISignInProps): Promise<void> => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          email: Yup.string().required('Email ou usuário obrigatório'),
          password: Yup.string().required('Senha obrogatória'),
        });

        await schema.validate(data, { abortEarly: false });

        addToast({
          title: 'Processando login',
          description: 'Aguarde ...',
        });

        const response = await api.post('session/adm', {
          email: data.email,
          password: data.password,
        });

        await signIn({
          token: response.data.token,
          user: response.data.user,
          refresh_token: response.data.refresh_token,
        });

        // `signIn` sempre limpa a preferência de viewMode salva -- pra quem
        // tem `manage_platform_gyms`, o modo padrão após login é "platform"
        // (mesma regra de `useAuth`/`Painel`), então o destino inicial
        // precisa seguir essa mesma regra em vez de sempre cair no
        // dashboard da academia.
        const hasPlatformPermission =
          !!response.data.user.permissions?.includes('manage_platform_gyms');

        navigate(hasPlatformPermission ? '/admin/gyms' : '/dashboard');
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        if (err?.response?.status === 400) {
          addToast({
            type: 'error',
            title: 'Credenciais inválidas',
            description: 'Verifique suas credenciais e tente novamente..',
          });
          return;
        }

        if (err?.response?.status === 401) {
          addToast({
            type: 'error',
            title: 'Nivel de acesso',
            description:
              'Acesso permitido apenas administradores e funcionários. Para alunos, acesse o app mobile.',
          });
          return;
        }

        if (err?.response?.status === 423) {
          addToast({
            type: 'error',
            title: 'Acesso bloqueado',
            description:
              'Usuário ou Academia bloqueados. Entre em contato com o administrador do sistema.',
          });
          return;
        }

        if (err?.response?.status === 403) {
          addToast({
            type: 'error',
            title: 'Painel não disponível',
            description:
              err?.response?.data?.message ||
              'Painel web não está disponível para esta academia.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na autenticação',
          description:
            'Ocorreu um erro ao fazer login, cheque as credenciais e tente novamente.',
        });
      }
    },
    [addToast],
  );

  return (
    <AuthLayout
      eyebrow="Desempenho de ponta"
      title={
        <>
          GESTÃO <span>INTELIGENTE</span>
        </>
      }
      subtitle="Acesse o painel administrativo para acompanhar alunos, treinos e o desempenho da sua academia em um só lugar."
    >
      <Header>
        <Title>Bem-vindo de volta</Title>
        <StatusTag>
          <span className="dot" />
          <span className="label">Sistema ativo</span>
        </StatusTag>
      </Header>

      <Form ref={formRef} onSubmit={handleSubmit}>
        <InputLogin
          name="email"
          type="text"
          inputLabel="Email ou usuário"
          placeholder="exemplo@dominio.com"
          icon={FiMail}
        />

        <InputLogin
          name="password"
          type="password"
          placeholder="**********"
          inputLabel="Senha"
          icon={FiLock}
        />

        <ContainerLink>
          <span>Esqueceu sua senha? </span>
          <span className="link" onClick={() => navigate('/forgot-password')}>
            Clique aqui
          </span>
        </ContainerLink>
        <ContainerLink>
          <span>Possui um código de cadastro? </span>
          <span className="link" onClick={() => navigate('/sign-up')}>
            Clique aqui
          </span>
        </ContainerLink>

        <ButtonSignIn name="submit" value="Entrar" />
      </Form>
    </AuthLayout>
  );
};

export { SignIn };
